import objectCardsApi from './api';
import {submitCommentFiles} from '@/utils/commentSubmission';

function defaultFilters() {
    return {
        q: ``,
        status: null,
        issue: null,
        curator_id: null,
        main_staff_id: null,
        has_access_user_id: null
    };
}

function defaultFilterOptions() {
    return {
        statuses: [],
        issues: [],
        countries: [],
        curators: [],
        staff: [],
        has_access_users: []
    };
}

function defaultAccessSettings() {
    return {
        access_config: null,
        can_manage_access: false,
        can_list: false,
        can_access_preparations: false,
        can_read: false,
        can_create: false,
        can_update: false,
        can_delete: false
    };
}

function listParams(state, options = {}) {
    const params = Object.assign({}, state.filters, {
        limit: state.limit,
        offset: state.offset,
        sort_by: state.sortBy,
        sort_desc: state.sortDesc
    });

    if (options.filterOptions === false) {
        params.filter_options = false;
    } else {
        params.filter_options = true;
    }

    Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined || params[key] === ``) {
            delete params[key];
        }
    });

    return params;
}

export default {
    namespaced: true,
    state: {
        items: [],
        total: 0,
        limit: 50,
        offset: 0,
        sortBy: `name`,
        sortDesc: false,
        filters: defaultFilters(),
        filterOptions: defaultFilterOptions(),
        selected: null,
        progress: null,
        consumption: {
            fuel: [],
            materials: []
        },
        accessSettings: defaultAccessSettings(),
        activity: {
            items: [],
            comments: [],
            history: [],
            total: 0,
            limit: 50,
            offset: 0,
            has_more: false,
            legacy_notice: false
        },
        loading: false,
        cardLoading: false,
        activityLoading: false,
        progressLoading: false,
        consumptionLoading: false,
        reportDownloading: false
    },
    mutations: {
        setTelegramState(state, {buildingId, data}) {
            if (!state.selected || Number(state.selected.building_id) !== Number(buildingId)) return;
            // Keep the card reference: its watcher fills editable object/process forms.
            state.selected.telegram_groups = data.groups;
            if (Object.prototype.hasOwnProperty.call(data, 'object_group')) state.selected.object_group = data.object_group;
        },
        setList(state, payload) {
            const source = payload || {};
            state.items = Array.isArray(source.items) ? source.items : [];
            state.total = Number(source.total) || 0;
            state.limit = Number(source.limit) || state.limit;
            state.offset = Number(source.offset) || 0;
            if (Object.prototype.hasOwnProperty.call(source, `filter_options`)) {
                state.filterOptions = Object.assign(defaultFilterOptions(), source.filter_options || {});
            }
        },
        setFilters(state, filters) {
            state.filters = Object.assign({}, state.filters, filters || {});
        },
        resetFilters(state) {
            state.filters = defaultFilters();
            state.offset = 0;
        },
        setPagination(state, {limit, offset}) {
            if (limit !== undefined) {
                state.limit = Number(limit) || state.limit;
            }
            state.offset = Number(offset) || 0;
        },
        setSort(state, {sortBy, sortDesc}) {
            state.sortBy = sortBy || `name`;
            state.sortDesc = Boolean(sortDesc);
            state.offset = 0;
        },
        setSelected(state, value) {
            state.selected = value || null;
        },
        setProgress(state, value) {
            state.progress = value || null;
        },
        setConsumption(state, value) {
            const source = value || {};
            state.consumption = {
                fuel: Array.isArray(source.fuel) ? source.fuel : [],
                materials: Array.isArray(source.materials) ? source.materials : []
            };
        },
        setAccessSettings(state, value) {
            state.accessSettings = Object.assign(defaultAccessSettings(), value || {});
        },
        setActivity(state, value) {
            const source = value || {};
            state.activity = {
                items: Array.isArray(source.items) ? source.items : [],
                comments: Array.isArray(source.comments) ? source.comments : [],
                history: Array.isArray(source.history) ? source.history : [],
                total: Number(source.total) || 0,
                limit: Number(source.limit) || 50,
                offset: Number(source.offset) || 0,
                has_more: source.has_more === true,
                legacy_notice: source.legacy_notice === true
            };
        },
        appendActivity(state, value) {
            const source = value || {};
            const itemKey = item => `${item.type || (item.action ? `log` : `comment`)}_${item.id}`;
            const existingKeys = new Set((state.activity.items || []).map(itemKey));
            const nextItems = (source.items || []).filter(item => !existingKeys.has(itemKey(item)));
            const commentIds = new Set((state.activity.comments || []).map(item => Number(item.id)));
            const historyIds = new Set((state.activity.history || []).map(item => Number(item.id)));
            state.activity.items = (state.activity.items || []).concat(nextItems);
            state.activity.comments = (state.activity.comments || []).concat(
                (source.comments || []).filter(item => !commentIds.has(Number(item.id)))
            );
            state.activity.history = (state.activity.history || []).concat(
                (source.history || []).filter(item => !historyIds.has(Number(item.id)))
            );
            state.activity.total = Number(source.total) || state.activity.total;
            state.activity.limit = Number(source.limit) || state.activity.limit;
            state.activity.offset = Number(source.offset) || state.activity.offset;
            state.activity.has_more = source.has_more === true;
            state.activity.legacy_notice = source.legacy_notice === true;
        },
        setLoading(state, value) {
            state.loading = Boolean(value);
        },
        setCardLoading(state, value) {
            state.cardLoading = Boolean(value);
        },
        setActivityLoading(state, value) {
            state.activityLoading = Boolean(value);
        },
        setProgressLoading(state, value) {
            state.progressLoading = Boolean(value);
        },
        setConsumptionLoading(state, value) {
            state.consumptionLoading = Boolean(value);
        },
        setReportDownloading(state, value) {
            state.reportDownloading = Boolean(value);
        }
    },
    actions: {
        async fetchList({commit, state}, options = {}) {
            const silent = options.silent === true;
            if (!silent) {
                commit(`setLoading`, true);
            }
            try {
                const payload = await objectCardsApi.list(listParams(state, options));
                commit(`setList`, payload);
                return payload;
            } finally {
                if (!silent) {
                    commit(`setLoading`, false);
                }
            }
        },
        async fetchAccessSettings({commit}) {
            const data = await objectCardsApi.getAccessSettings();
            commit(`setAccessSettings`, data);
            return data;
        },
        async updateAccessSettings({commit}, accessConfig) {
            const data = await objectCardsApi.updateAccessSettings(accessConfig);
            commit(`setAccessSettings`, data);
            return data;
        },
        fetchObjectAccess(_, params = {}) {
            return objectCardsApi.getObjectAccess(params);
        },
        updateObjectAccess(_, {id, accessConfig}) {
            return objectCardsApi.updateObjectAccess(id, accessConfig);
        },
        async applyFilters({commit, dispatch}, filters) {
            commit(`setFilters`, filters);
            commit(`setPagination`, {offset: 0});
            return dispatch(`fetchList`);
        },
        async setPage({commit, dispatch, state}, page) {
            const currentPage = Math.max(1, Number(page) || 1);
            commit(`setPagination`, {
                limit: state.limit,
                offset: (currentPage - 1) * state.limit
            });
            return dispatch(`fetchList`, {filterOptions: false});
        },
        async setLimit({commit, dispatch, state}, limit) {
            commit(`setPagination`, {
                limit: Number(limit) || state.limit,
                offset: 0
            });
            return dispatch(`fetchList`, {filterOptions: false});
        },
        async setSort({commit, dispatch}, payload) {
            commit(`setSort`, payload || {});
            return dispatch(`fetchList`, {filterOptions: false});
        },
        async openObject({commit, dispatch}, id) {
            commit(`setCardLoading`, true);
            commit(`setSelected`, null);
            commit(`setProgress`, null);
            commit(`setConsumption`, null);
            commit(`setActivity`, null);
            try {
                const card = await objectCardsApi.get(id);
                commit(`setSelected`, card);
                await dispatch(`fetchActivity`, id);
                return card;
            } finally {
                commit(`setCardLoading`, false);
            }
        },
        async fetchActivity({commit, state}, payload) {
            const source = payload && typeof payload === `object` ? payload : {id: payload};
            const id = source.id;
            const append = source.append === true;
            const offset = append ? (state.activity.items || []).length : 0;
            commit(`setActivityLoading`, true);
            try {
                const activity = await objectCardsApi.activity(id, {limit: 50, offset});
                commit(append ? `appendActivity` : `setActivity`, activity);
                return activity;
            } finally {
                commit(`setActivityLoading`, false);
            }
        },
        async fetchProgress({commit}, id) {
            commit(`setProgressLoading`, true);
            try {
                const progress = await objectCardsApi.progress(id);
                commit(`setProgress`, progress);
                return progress;
            } finally {
                commit(`setProgressLoading`, false);
            }
        },
        async createTarget({state, dispatch}, payload) {
            const result = await objectCardsApi.createTarget(payload);
            if (state.selected && state.selected.building_id) {
                await dispatch(`fetchActivity`, state.selected.building_id);
            }
            return result;
        },
        async updateTarget({state, dispatch}, payload) {
            const result = await objectCardsApi.updateTarget(payload);
            if (state.selected && state.selected.building_id) {
                await dispatch(`fetchActivity`, state.selected.building_id);
            }
            return result;
        },
        async deleteTarget({state, dispatch}, payload) {
            const result = await objectCardsApi.deleteTarget(payload);
            if (state.selected && state.selected.building_id) {
                await dispatch(`fetchActivity`, state.selected.building_id);
            }
            return result;
        },
        async setTargetStatus({state, dispatch}, {id, isActive}) {
            const result = await objectCardsApi.setTargetStatus(id, isActive);
            if (state.selected && state.selected.building_id) {
                await dispatch(`fetchActivity`, state.selected.building_id);
            }
            return result;
        },
        async createMaterialTarget({state, dispatch}, payload) {
            const result = await objectCardsApi.createMaterialTarget(payload);
            if (state.selected && state.selected.building_id) {
                await dispatch(`fetchActivity`, state.selected.building_id);
            }
            return result;
        },
        async updateMaterialTarget({state, dispatch}, {id, payload}) {
            const result = await objectCardsApi.updateMaterialTarget(id, payload);
            if (state.selected && state.selected.building_id) {
                await dispatch(`fetchActivity`, state.selected.building_id);
            }
            return result;
        },
        async deleteMaterialTarget({state, dispatch}, id) {
            const result = await objectCardsApi.deleteMaterialTarget(id);
            if (state.selected && state.selected.building_id) {
                await dispatch(`fetchActivity`, state.selected.building_id);
            }
            return result;
        },
        async setMaterialTargetStatus({state, dispatch}, {id, isActive}) {
            const result = await objectCardsApi.setMaterialTargetStatus(id, isActive);
            if (state.selected && state.selected.building_id) {
                await dispatch(`fetchActivity`, state.selected.building_id);
            }
            return result;
        },
        async fetchConsumption({commit}, id) {
            commit(`setConsumptionLoading`, true);
            try {
                const consumption = await objectCardsApi.consumption(id);
                commit(`setConsumption`, consumption);
                return consumption;
            } finally {
                commit(`setConsumptionLoading`, false);
            }
        },
        async downloadReport({commit}, payload) {
            const source = payload || {};
            const buildingProcessId = Number(source.buildingProcessId);
            if (!Number.isInteger(buildingProcessId) || buildingProcessId <= 0) {
                return null;
            }
            commit(`setReportDownloading`, true);
            try {
                const response = await objectCardsApi.downloadReport({
                    buildingProcessId,
                    chatId: source.chatId
                });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement(`a`);
                const objectName = String(source.objectName || `object`)
                    .replace(/[\\/:*?"<>|]+/g, `_`)
                    .replace(/\s+/g, `_`)
                    .slice(0, 80);
                link.href = url;
                link.setAttribute(`download`, `Отчет_${objectName || buildingProcessId}_${buildingProcessId}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                return {success: true};
            } finally {
                commit(`setReportDownloading`, false);
            }
        },
        async createObject({commit, dispatch}, payload) {
            const source = payload || {};
            let card = await objectCardsApi.create(source.object || source);
            if (source.process && source.process.enabled === true) {
                card = await objectCardsApi.update(card.building_id, source);
            }
            commit(`setSelected`, card);
            commit(`setProgress`, null);
            commit(`setConsumption`, null);
            await dispatch(`fetchList`, {silent: true});
            return card;
        },
        async updateObject({commit, dispatch}, {id, payload}) {
            const card = await objectCardsApi.update(id, payload);
            commit(`setSelected`, card);
            commit(`setProgress`, null);
            commit(`setConsumption`, null);
            await dispatch(`fetchActivity`, id);
            await dispatch(`fetchList`, {silent: true});
            return card;
        },
        async deleteObject({commit, dispatch}, id) {
            await objectCardsApi.deleteObject(id);
            commit(`setSelected`, null);
            commit(`setProgress`, null);
            commit(`setConsumption`, null);
            commit(`setActivity`, null);
            await dispatch(`fetchList`, {silent: true});
            return {success: true};
        },
        async addComment({dispatch}, {id, text, files, draft = {}}) {
            await submitCommentFiles(draft, text, files, {
                create: value => objectCardsApi.addComment(id, value),
                update: (commentId, value) => objectCardsApi.updateComment(id, commentId, value),
                upload: (commentId, file) => objectCardsApi.addCommentFiles(id, commentId, [file], {initial: true})
            });
            return dispatch(`fetchActivity`, id);
        },
        async updateComment({dispatch}, {id, commentId, text}) {
            await objectCardsApi.updateComment(id, commentId, text);
            return dispatch(`fetchActivity`, id);
        },
        async deleteComment({dispatch}, {id, commentId}) {
            await objectCardsApi.deleteComment(id, commentId);
            return dispatch(`fetchActivity`, id);
        },
        async deleteCommentFile({dispatch}, {id, commentId, fileId}) {
            await objectCardsApi.deleteCommentFile(id, commentId, fileId);
            return dispatch(`fetchActivity`, id);
        }
    }
};
